import {
  WORK_ORDER_PRIORITIES,
  WORK_ORDER_PRIORITY_LABELS,
  WORK_ORDER_SOURCE_LABELS,
  WORK_ORDER_STATUS_LABELS,
  WORK_ORDER_STATUSES,
  WORK_ORDER_TYPE_LABELS,
  type WorkOrderPriorityCode,
  type WorkOrderSummary,
} from "@station/contracts";
import {
  Alert,
  Button,
  Checkbox,
  DataTable,
  type DataTableColumn,
  EmptyState,
  FormField,
  Input,
  Label,
  MoneyText,
  Select,
} from "@station/ui";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Plus, Wrench } from "lucide-react";
import { type ReactElement, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
  DEFAULT_WORK_ORDER_FILTERS,
  OPEN_ALL,
  type WorkOrderFilters,
} from "../../../../features/maintenance/maintenance-values.js";
import { useMaintenancePermissions, useWorkOrders } from "../../../../features/maintenance/queries.js";
import {
  MaintenancePage,
  PriorityBadge,
  SlaBadge,
  StationFilterField,
  WorkOrderStatusBadge,
} from "../../../../features/maintenance/shared.js";
import { NewWorkOrderDialog } from "../../../../features/maintenance/work-order-dialogs.js";
import { quickActionSearch, useOpenFromQuickAction } from "../../../../lib/hooks.js";
import { useCursorPages } from "../../../../lib/cursor-pages.js";
import { errorMessage } from "../../../../lib/errors.js";
import { formatDateTime } from "../../../../lib/format.js";

const searchSchema = z.object({
  station: z.string().uuid().optional().catch(undefined),
  status: z.string().max(80).optional().catch(undefined),
  priority: z.enum(WORK_ORDER_PRIORITIES).optional().catch(undefined),
  sla: z.enum(["1"]).optional().catch(undefined),
  q: z.string().max(80).optional().catch(undefined),
  ...quickActionSearch,
});

export const Route = createFileRoute("/_app/bakim/is-emirleri/")({
  validateSearch: (search) => searchSchema.parse(search),
  component: WorkOrdersRoute,
});

/**
 * Bakım / arıza iş emirleri (Prompt 23 web, `GET /maintenance/work-orders` imleçli): varsayılan AÇIK iş emirleri; süzgeçler
 * URL'de (istasyon, durum, öncelik, SLA aşımı, arama). Yeni iş emri penceresi istasyon + varlık + öncelik + atama alır.
 */
function WorkOrdersRoute(): ReactElement {
  const { t } = useTranslation();
  const permissions = useMaintenancePermissions();
  const search = Route.useSearch();
  const [creating, setCreating] = useState(false);
  useOpenFromQuickAction(search.yeni === "1", () => setCreating(true), permissions.canWork);
  return (
    <MaintenancePage
      title={t("menu.maintenanceWorkOrders")}
      description={t("maintenance.workOrders.description")}
      actions={
        permissions.canWork ? (
          <Button onClick={() => setCreating(true)}>
            <Plus aria-hidden="true" />
            {t("maintenance.workOrders.new")}
          </Button>
        ) : null
      }
    >
      <WorkOrderList />
      {creating ? (
        <NewWorkOrderDialog open onOpenChange={setCreating} defaultStationId={search.station ?? null} />
      ) : null}
    </MaintenancePage>
  );
}

function WorkOrderList(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const slaId = useId();
  const filters: WorkOrderFilters = {
    ...DEFAULT_WORK_ORDER_FILTERS,
    stationId: search.station ?? null,
    status: search.status ?? OPEN_ALL,
    priority: search.priority ?? null,
    slaBreached: search.sla === "1",
    search: search.q ?? "",
  };
  const [text, setText] = useState(filters.search);
  const pages = useCursorPages(JSON.stringify(filters));
  const list = useWorkOrders(filters, pages.cursor);
  const setFilters = (next: WorkOrderFilters) =>
    void navigate({
      to: ".",
      search: {
        ...(next.stationId ? { station: next.stationId } : {}),
        ...(next.status !== OPEN_ALL ? { status: next.status } : {}),
        ...(next.priority ? { priority: next.priority } : {}),
        ...(next.slaBreached ? { sla: "1" } : {}),
        ...(next.search ? { q: next.search } : {}),
      } as never,
      replace: true,
    });

  const columns = useMemo<DataTableColumn<WorkOrderSummary>[]>(
    () => [
      {
        id: "workOrder",
        header: t("maintenance.fields.workOrder"),
        hideable: false,
        cell: (row) => (
          <span className="grid min-w-56 gap-0.5">
            <Link
              to="/bakim/is-emirleri/$id"
              params={{ id: row.id }}
              className="font-medium underline underline-offset-4"
            >
              {row.title}
            </Link>
            <span className="text-xs text-muted-foreground">
              {row.workOrderNo} · {WORK_ORDER_TYPE_LABELS[row.workType]} ·{" "}
              {WORK_ORDER_SOURCE_LABELS[row.source]}
            </span>
          </span>
        ),
      },
      {
        id: "station",
        header: t("maintenance.fields.station"),
        cell: (row) => (
          <span className="grid gap-0.5">
            <span className="whitespace-nowrap">{row.stationCode}</span>
            <span className="text-xs text-muted-foreground">
              {row.asset?.name ?? t("maintenance.stationWide")}
            </span>
          </span>
        ),
      },
      {
        id: "priority",
        header: t("maintenance.fields.priority"),
        cell: (row) => <PriorityBadge priority={row.priority} />,
      },
      {
        id: "status",
        header: t("maintenance.fields.status"),
        cell: (row) => (
          <span className="grid justify-items-start gap-1">
            <WorkOrderStatusBadge status={row.status} />
            <SlaBadge sla={row.sla} />
          </span>
        ),
      },
      {
        id: "resolveDueAt",
        header: t("maintenance.fields.resolveDueAt"),
        cell: (row) => <span className="tabular whitespace-nowrap">{formatDateTime(row.resolveDueAt)}</span>,
      },
      {
        id: "assignee",
        header: t("maintenance.fields.assignee"),
        cell: (row) => (
          <span className="grid gap-0.5">
            <span>{row.serviceCounterparty?.name ?? "—"}</span>
            {row.assignedUser ? (
              <span className="text-xs text-muted-foreground">{row.assignedUser.name}</span>
            ) : null}
          </span>
        ),
      },
      {
        id: "cost",
        header: t("maintenance.fields.totalCost"),
        align: "right",
        cell: (row) => <MoneyText value={row.totalCost} />,
      },
      {
        id: "createdAt",
        header: t("maintenance.fields.createdAt"),
        defaultVisible: false,
        cell: (row) => <span className="tabular whitespace-nowrap">{formatDateTime(row.createdAt)}</span>,
      },
    ],
    [t],
  );

  return (
    <DataTable
      caption={t("menu.maintenanceWorkOrders")}
      columns={columns}
      rows={list.data?.items ?? []}
      rowKey={(row) => row.id}
      loading={list.isPending}
      error={list.error ? <Alert tone="danger">{errorMessage(list.error)}</Alert> : undefined}
      filters={
        <>
          <StationFilterField
            value={filters.stationId}
            onChange={(stationId) => setFilters({ ...filters, stationId })}
          />
          <FormField label={t("maintenance.fields.status")} className="w-44">
            {(control) => (
              <Select
                {...control}
                value={filters.status}
                onChange={(event) => setFilters({ ...filters, status: event.target.value })}
              >
                <option value={OPEN_ALL}>{t("maintenance.workOrders.openAll")}</option>
                <option value="">{t("common.all")}</option>
                {WORK_ORDER_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {WORK_ORDER_STATUS_LABELS[status]}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
          <FormField label={t("maintenance.fields.priority")} className="w-36">
            {(control) => (
              <Select
                {...control}
                value={filters.priority ?? ""}
                onChange={(event) =>
                  setFilters({
                    ...filters,
                    priority: (event.target.value || null) as WorkOrderPriorityCode | null,
                  })
                }
              >
                <option value="">{t("common.all")}</option>
                {WORK_ORDER_PRIORITIES.map((priority) => (
                  <option key={priority} value={priority}>
                    {WORK_ORDER_PRIORITY_LABELS[priority]}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
          <form
            className="flex items-end gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              setFilters({ ...filters, search: text.trim() });
            }}
          >
            <FormField label={t("common.search")} className="w-52">
              {(control) => (
                <Input
                  {...control}
                  type="search"
                  value={text}
                  placeholder={t("maintenance.workOrders.searchPlaceholder")}
                  onChange={(event) => setText(event.target.value)}
                />
              )}
            </FormField>
          </form>
          <div className="flex h-10 items-center gap-2">
            <Checkbox
              id={slaId}
              checked={filters.slaBreached}
              onChange={(event) => setFilters({ ...filters, slaBreached: event.target.checked })}
            />
            <Label htmlFor={slaId}>{t("maintenance.workOrders.slaOnly")}</Label>
          </div>
        </>
      }
      emptyState={
        <EmptyState
          headingLevel={2}
          icon={<Wrench />}
          title={t("maintenance.workOrders.emptyTitle")}
          description={t("maintenance.workOrders.emptyDescription")}
        />
      }
      pagination={{
        hasPrevious: pages.hasPrevious,
        hasNext: list.data?.page.hasMore ?? false,
        onPrevious: pages.previous,
        onNext: () => pages.next(list.data?.page.nextCursor),
        pageLabel: t("common.pageNumber", { page: pages.pageNumber }),
      }}
    />
  );
}
